/**
 * Project operations.
 *
 * Projects are their own container rather than entries, so they get their own verbs. They carry no
 * metadata bag and no links, which is why this file is short and does not reach for `patch.ts`.
 */

import { newId } from '../ids'
import type { AppDocument, ProjectStatus } from '../schema'

export interface ProjectDraft {
  name: string
  description?: string
  status?: ProjectStatus
  areaId?: string | null
}

export interface ProjectChange {
  name?: string
  description?: string
  status?: ProjectStatus
}

/** Create a project. Returns its id. */
export function createProject(doc: AppDocument, draft: ProjectDraft): string {
  const id = newId()

  doc.projects[id] = {
    id,
    name: draft.name,
    description: draft.description ?? '',
    areaId: draft.areaId ?? null,
    status: draft.status ?? 'active',
    createdAt: new Date().toISOString(),
  }

  return id
}

/** Change a project. Returns `false` when there is no such project. */
export function updateProject(
  doc: AppDocument,
  id: string,
  change: ProjectChange,
): boolean {
  const project = doc.projects[id]
  if (!project) return false

  if (change.name !== undefined) project.name = change.name
  if (change.description !== undefined) {
    project.description = change.description
  }
  if (change.status !== undefined) project.status = change.status

  return true
}

/** Archive a project. Returns `false` when there is no such project. */
export function archiveProject(doc: AppDocument, id: string): boolean {
  const project = doc.projects[id]
  if (!project) return false

  project.archivedAt = new Date().toISOString()

  return true
}
